// @flow
import { createSelector, Selector } from 'reselect';
import pluralize from 'pluralize';

import { DEFAULT_GROUP } from '../ui/ui.const.js';
import { EVENT_EDIT } from './calendar.consts.js';

import { getOption, Option } from '../form/select.js';
import {
  compareObjects,
  compareResources,
  getAllCheckedInGroup,
  getFullName,
  getMinutes,
  groupBy,
  int,
  isChecked,
  isUnmoved,
  makeSort,
  parseTime
} from './calendar.model.js';

const selectEvent = ({ event }) => event;
export const getEventAircraft = ({ event }) =>
  Array.isArray(event.resources) ? event.resources.find(item => item.aircraftID) : null;

// ---------- FILTER TIMEZONES ---------- //
export const selectTimeZonesOptions: Selector<ReduxState, void, Option[]> = createSelector(
  state => state.groupedTimezones,
  groupedTimezones =>
    Object.keys(groupedTimezones).reduce((acc, name) => {
      const zones =
        name === 'Recent'
          ? Object.keys(groupedTimezones[name].timeZones)
          : groupedTimezones[name].timeZones;

      acc = acc.concat(
        getOption(groupedTimezones[name].name, groupedTimezones[name].name, {
          disabled: true,
          isGroup: true
        })
      );

      acc = acc.concat(
        zones.map(z => {
          const zone = z.id ? z : groupedTimezones[name].timeZones[z];
          return getOption(`${zone.offsetString} ${zone.abbr} ${zone.id}`, zone.id);
        })
      );

      return acc;
    }, [])
);

// ---------- FILTER ORGANIZATION ---------- //
export const selectFilterOrgOptions: Selector<ReduxState, void, Option[]> = createSelector(
  state => state.organizations,
  state => state.organizationsList,
  state => state.filterForm,
  (organizations, list, form) =>
    list.map(org =>
      getOption(organizations[org].organizationName, organizations[org].organizationID, {
        checked: isChecked(form, 'organizations', organizations[org].organizationID)
      })
    )
);

export const selectOrgSelectText: Selector<ReduxState, void, Option[]> = createSelector(
  state => state.filterForm,
  form => form && form.organizations.map(({ label }) => label).join(', ')
);

// ---------- FILTER LOCATION ---------- //
export const selectFilterLocGroupedOptions: Selector<ReduxState, void, Option[]> = createSelector(
  state => state.locationsList,
  state => state.locations,
  state => state.organizations,
  state => state.filterForm,
  (list, locations, organizations, form) =>
    list.reduce((acc, id) => {
      const loc = locations[id];
      if (!organizations[loc.organizationID]) return acc;
      const checked = isChecked(form, 'locations', id);

      if (!acc[loc.organizationUnique]) {
        acc[loc.organizationUnique] = [
          getOption(`[${loc.organizationName}]`, loc.organizationID, { isGroup: true }),
          getOption(loc.locationName, id, { padded: true, checked })
        ];
      } else {
        acc[loc.organizationUnique] = [
          ...acc[loc.organizationUnique],
          getOption(loc.locationName, id, { padded: true, checked })
        ];
      }

      return acc;
    }, {})
);

export const selectCheckedAllLocInOrg: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterLocGroupedOptions,
  groupedLocations => getAllCheckedInGroup(groupedLocations)
);

export const selectOrgByUnique: Selector<ReduxState, void, Option[]> = createSelector(
  state => state.organizations,
  state => state.organizationsList,
  (organizations, list) =>
    list.reduce(
      (acc, id) => ({
        ...acc,
        [organizations[id].organizationUnique]: organizations[id]
      }),
      {}
    )
);

export const selectFilterLocOptions: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterLocGroupedOptions,
  selectCheckedAllLocInOrg,
  state => state.filterForm,
  selectOrgByUnique,
  (groupedLocations, checkedAll, filterForm, organizations) =>
    Object.keys(groupedLocations).reduce((acc, orgUnique) => {
      if (checkedAll[orgUnique]) groupedLocations[orgUnique][0].checked = true;
      if (!filterForm || !filterForm.organizations.length) {
        acc = acc.concat(groupedLocations[orgUnique]);
      } else if (filterForm) {
        acc = !!filterForm.organizations.find(
          val => val.value === organizations[orgUnique].organizationID
        )
          ? acc.concat(groupedLocations[orgUnique])
          : acc;
      }
      return acc;
    }, [])
);

export const selectLocSelectText: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterLocGroupedOptions,
  selectFilterLocOptions,
  state => state.filterForm,
  state => state.locations,
  selectOrgByUnique,
  (groupedLocations, options, form, locations, organizations) => {
    if (form) {
      const optionsLength = options.reduce((acm, opt) => (!opt.isGroup ? ++acm : acm), 0);
      if (form.locations.length === optionsLength) {
        return 'All locations are selected';
      }
      if (!form.locations.length) {
        return false;
      }
      const groupedLoc = groupBy(form.locations, locations, 'organizationUnique');

      return Object.keys(groupedLocations).reduce((acc, group) => {
        if (groupedLoc[group]) {
          if (groupedLocations[group].length - 1 === groupedLoc[group].length) {
            acc = acc.concat(`${acc.length ? ', ' : ''}[${organizations[group].organizationName}]`);
          } else {
            acc = `[${acc.concat(
              groupedLoc[group].reduce((accum, locat, i) => {
                if (!accum.length)
                  accum = accum.concat(`[${organizations[group].organizationName}]:`);
                accum = accum.concat(
                  ` ${locations[locat.value].locationName}${
                    i !== groupedLoc[group].length - 1 ? ' ,' : ''
                  }`
                );
                return accum;
              }, '')
            )}],`;
          }
        }
        return acc;
      }, '');
    }
  }
);

// ---------- FILTER PILOT ---------- //
// for pilots referece keys is ${organizationID}-${pilotID}
export const selectFilterPilotGroupedOptions: Selector<ReduxState, void, Option[]> = createSelector(
  state => state.pilotsList,
  state => state.pilots,
  state => state.organizations,
  state => state.filterForm,
  (list, pilots, organizations, form) =>
    list.reduce((acc, id) => {
      const pilot = pilots[id];
      if (!organizations[pilot.organizationID]) return acc;
      const checked = isChecked(form, 'pilots', id);
      const pilotName = `${pilot.pilotName} ${pilot.pilotMiddlename || ''} ${pilot.pilotSurname}`;

      if (!acc[pilot.organizationID]) {
        acc[pilot.organizationID] = [
          getOption(`[${pilot.organizationName}]`, pilot.organizationID, { isGroup: true }),
          getOption(pilotName, id, { padded: true, checked })
        ];
      } else {
        acc[pilot.organizationID] = [
          ...acc[pilot.organizationID],
          getOption(pilotName, id, { padded: true, checked })
        ];
      }

      return acc;
    }, {})
);

export const selectCheckedAllPilotInOrg: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterPilotGroupedOptions,
  groupedPilots => getAllCheckedInGroup(groupedPilots)
);

export const selectFilterPilotOptions: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterPilotGroupedOptions,
  selectCheckedAllPilotInOrg,
  state => state.filterForm,
  (groupedPilots, checkedAll, form) =>
    Object.keys(groupedPilots).reduce((acc, orgId) => {
      if (checkedAll[orgId]) groupedPilots[orgId][0].checked = true;
      if (!form || !form.organizations.length) {
        acc = acc.concat(groupedPilots[orgId]);
      } else if (form) {
        acc = !!form.organizations.find(val => val.value === orgId)
          ? acc.concat(groupedPilots[orgId])
          : acc;
      }
      return acc;
    }, [])
);

export const selectPilotSelectText: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterPilotGroupedOptions,
  selectFilterPilotOptions,
  state => state.filterForm,
  state => state.pilots,
  state => state.organizations,
  (groupedPilots, options, form, pilots, organizations) => {
    if (form) {
      const optionsLength = options.reduce((acm, opt) => (!opt.isGroup ? ++acm : acm), 0);
      if (form.pilots.length === optionsLength) {
        return 'All pilots are selected';
      }
      if (!form.pilots.length) {
        return false;
      }

      const groupedPilotsForm = groupBy(form.pilots, pilots, 'organizationID');

      return Object.keys(groupedPilots).reduce((acc, group, i) => {
        if (groupedPilotsForm[group]) {
          if (groupedPilots[group].length - 1 === groupedPilotsForm[group].length) {
            acc = acc.concat(`${acc.length ? ', ' : ''}[${organizations[group].organizationName}]`);
          } else {
            acc = `[${acc.concat(
              groupedPilotsForm[group].reduce((accum, pilot, i) => {
                const pilotName = `
                ${pilots[pilot.value].pilotName}
                ${pilots[pilot.value].pilotMiddlename || ''}
                ${pilots[pilot.value].pilotSurname}
              `;
                if (!accum.length)
                  accum = accum.concat(`[${organizations[group].organizationName}]:`);
                accum = accum.concat(
                  ` ${pilotName}${i !== groupedPilotsForm[group].length - 1 ? ' ,' : ''}`
                );
                return accum;
              }, '')
            )}],`;
          }
        }
        return acc;
      }, '');
    }
  }
);

// ---------- FILTER INSTRUCTOR ---------- //
// for instructor referece keys is ${locationID}-${resourceID}
export const selectFilterInstructorGroupedOptions: Selector<
  ReduxState,
  void,
  Option[]
> = createSelector(
  state => state.instructorsList,
  state => state.instructors,
  state => state.locations,
  state => state.filterForm,
  (list, instructors, locations, form) =>
    list.reduce((acc, id) => {
      const instructor = instructors[id];
      if (!locations[instructor.locationID]) return acc;

      const checked = isChecked(form, 'instructors', id);
      const instructorName = `
      ${instructor.instructorName}
      ${instructor.instructorMiddlename || ''}
      ${instructor.instructorSurname}
    `;

      if (!acc[instructor.locationID]) {
        acc[instructor.locationID] = [
          getOption(
            `[${instructor.locationName} [${instructor.organizationName}]]`,
            instructor.locationID,
            { isGroup: true }
          ),
          getOption(instructorName, id, { padded: true, checked })
        ];
      } else {
        acc[instructor.locationID] = [
          ...acc[instructor.locationID],
          getOption(instructorName, id, { padded: true, checked })
        ];
      }

      return acc;
    }, {})
);

export const selectCheckedAllInstructorInOrg: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterInstructorGroupedOptions,
  groupedInstructors => getAllCheckedInGroup(groupedInstructors)
);

export const selectFilterInstructorOptions: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterInstructorGroupedOptions,
  selectCheckedAllInstructorInOrg,
  state => state.filterForm,
  state => state.locations,
  (groupedInstructors, checkedAll, form, locations) =>
    Object.keys(groupedInstructors).reduce((acc, locId) => {
      if (checkedAll[locId]) groupedInstructors[locId][0].checked = true;
      if (!form || (!form.organizations.length && !form.locations.length)) {
        acc = acc.concat(groupedInstructors[locId]);
      } else if (form) {
        if (form.locations.length) {
          if (!!form.locations.find(val => val.value === locId)) {
            acc = acc.concat(groupedInstructors[locId]);
          }
        } else if (form.organizations.length) {
          if (!!form.organizations.find(val => val.value === locations[locId].organizationID)) {
            acc = acc.concat(groupedInstructors[locId]);
          }
        }
        return acc;
      }
      return acc;
    }, [])
);


export const selectFilterResourceGroupedOptions: Selector<
  ReduxState,
  void,
  Option[]
> = createSelector(
  state => state.resourcesList,
  state => state.resources,
  state => state.locations,
  state => state.filterForm,
  (list, resources, locations, form) =>
    list.reduce((acc, id) => {
      const resource = resources[id];

      const { resourceName, locationID, locationName, aircraftID, organizationName } = resource

      if (!aircraftID || !locationID) return acc;

      const checked = isChecked(form, 'resources', id);

      if (!acc[locationID]) {
        acc[locationID] = [
          getOption(
            `[${locationName} [${organizationName}]]`,
            locationID,
            { isGroup: true }
          ),
          getOption(resourceName, id, { padded: true, checked })
        ];
      } else {
        acc[locationID] = [
          ...acc[locationID],
          getOption(resourceName, id, { padded: true, checked })
        ];
      }

      return acc;
    }, {})
);

export const selectCheckedAllResourcesInOrg: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterResourceGroupedOptions,
  groupedResources => getAllCheckedInGroup(groupedResources)
);

export const selectFilterResourceOptions: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterResourceGroupedOptions,
  selectCheckedAllResourcesInOrg,
  state => state.filterForm,
  state => state.locations,
  (groupedResources, checkedAll, form, locations) => {
    return Object.keys(groupedResources).reduce((acc, locId) => {
      if (checkedAll[locId]) groupedResources[locId][0].checked = true;
      if (!form || (!form.organizations.length && !form.locations.length)) {
        acc = acc.concat(groupedResources[locId]);
      } else if (form) {
        if (form.locations.length) {
          if (!!form.locations.find(val => val.value === locId)) {
            acc = acc.concat(groupedResources[locId]);
          }
        } else if (form.organizations.length) {
          if (!!form.organizations.find(val => val.value === locations[locId].organizationID)) {
            acc = acc.concat(groupedResources[locId]);
          }
        }
        return acc;
      }
      return acc;
    }, [])
  }
);

export const selectResourcesSelectText: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterResourceGroupedOptions,
  selectFilterResourceOptions,
  state => state.filterForm,
  state => state.resources,
  state => state.locations,
  (groupedResources, options, form, resources, locations) => {

    if (form) {
      const optionsLength = options.reduce((acm, opt) => (!opt.isGroup ? ++acm : acm), 0);

      if (form.instructors.length === optionsLength) {
        return 'All resources are selected';
      }

      if (!form.resources.length) {
        return false;
      }

      const groupedResourcesForm = groupBy(form.resources, resources, 'locationID');
      return  Object.keys(groupedResources).reduce((acc, group, i) => {
        if (groupedResourcesForm[group]) {
          const groupName = `[${locations[group].locationName} [${locations[group].organizationName}]]`;
          if (groupedResources[group].length - 1 === groupedResourcesForm[group].length) {
            acc = acc.concat(`${acc.length ? ', ' : ''}${groupName}`);
          } else {
            acc = `[${acc.concat(
              groupedResourcesForm[group].reduce((accum, resource, i) => {
                if (!accum.length) accum = accum.concat(`${groupName}:`);
                accum = accum.concat(
                  ` ${resource.resourceName}${i !== groupedResourcesForm[group].length - 1 ? ' ,' : ''}`
                );
                return accum;
              }, '')
            )}],`;
          }
        }
        return acc;
      }, '');
    }
  }
);

export const selectInstructorSelectText: Selector<ReduxState, void, Option[]> = createSelector(
  selectFilterInstructorGroupedOptions,
  selectFilterInstructorOptions,
  state => state.filterForm,
  state => state.instructors,
  state => state.locations,
  (groupedInstructors, options, form, instructors, locations) => {
    if (form) {
      const optionsLength = options.reduce((acm, opt) => (!opt.isGroup ? ++acm : acm), 0);
      if (form.instructors.length === optionsLength) {
        return 'All instructors are selected';
      }
      if (!form.instructors.length) {
        return false;
      }

      const groupedInstructorsForm = groupBy(form.instructors, instructors, 'locationID');

      return Object.keys(groupedInstructors).reduce((acc, group, i) => {
        if (groupedInstructorsForm[group]) {
          const groupName = `[${locations[group].locationName} [${locations[group].organizationName}]]`;
          if (groupedInstructors[group].length - 1 === groupedInstructorsForm[group].length) {
            acc = acc.concat(`${acc.length ? ', ' : ''}${groupName}`);
          } else {
            acc = `[${acc.concat(
              groupedInstructorsForm[group].reduce((accum, instructor, i) => {
                const instructorName = `
                ${instructors[instructor.value].instructorName}
                ${instructors[instructor.value].instructorMiddlename || ''}
                ${instructors[instructor.value].instructorSurname}
              `;
                if (!accum.length) accum = accum.concat(`${groupName}:`);
                accum = accum.concat(
                  ` ${instructorName}${i !== groupedInstructorsForm[group].length - 1 ? ' ,' : ''}`
                );
                return accum;
              }, '')
            )}],`;
          }
        }
        return acc;
      }, '');
    }
  }
);

export const selectReservedResources: Selector<ReduxState, void, Option[]> = createSelector(
  state => state.resourcesList,
  state => state.resources,
  state => state.events,
  (list, resources, events) => {
    const reservedLevels = list.reduce((acum, id) => {
      acum[id] = {};

      const resource = resources[id];
      // Skip hidden/unpublished resources still referenced by an event:
      // acum[id] stays {} (set above), keeping both reduce passes safe.
      if (!resource) return acum;
      const sortedEvents = [...resource.events].sort((a, b) => {
        if (events[a].reserve === events[b].reserve) {
          return (
            parseInt(events[b].reservationModifyDate, 10) -
            parseInt(events[a].reservationModifyDate, 10)
          );
        }
        return parseInt(events[b].reserve, 10) - parseInt(events[a].reserve, 10);
      });

      sortedEvents.forEach(eventID => {
        if (parseInt(events[eventID].reserve, 10) && (resource.aircraftID || resource.simulatorID))
          acum[id][eventID] = 1;
      });

      sortedEvents.forEach(eventID => {
        const event = events[eventID];
        if (isUnmoved(event, id)) return;
        let level = acum[id][eventID] || 0;

        sortedEvents.forEach(innerID => {
          const diffEvent = eventID !== innerID;
          if (!diffEvent) return;

          const innerEvent = events[innerID];
          const innerStart = innerEvent.startTime;
          const innerFin =   innerEvent.finishTime;
          const eventStart = event.startTime;
          const eventFin =   event.finishTime;

          const innerStartBeforeOrAtEventStart = innerStart <= eventStart;
          const innerFinAfterEventStart = eventStart < innerFin;

          const innerStartBeforeEventFin = innerStart < eventFin;
          const innerFinAfterOrAtEventFin = eventFin <= innerFin;

          const innerStartAfterOrAtEventStart = innerStart >= eventStart;
          const innerFinBeforeOrAtEventFin = eventFin >= innerFin;


          const innerFrontOverlapsEvent = innerStartBeforeOrAtEventStart && innerFinAfterEventStart;
          const innerEventOverlapsEvent = innerStartBeforeEventFin && innerFinAfterOrAtEventFin;
          const innerEventInsideEvent = innerStartAfterOrAtEventStart && innerFinBeforeOrAtEventFin;

          if (diffEvent && (innerFrontOverlapsEvent || innerEventOverlapsEvent || innerEventInsideEvent)) {
            level = acum[id][innerID] <= level
              ? (acum[id][innerID] || 0) + 1
              : level;
          }
        });

        acum[id][eventID] = level;
      });

      return acum;
    }, {});

    return list.reduce((acc, resID) => {
      const sorted = Object.keys(reservedLevels[resID]).sort(
        (a, b) => reservedLevels[resID][a] - reservedLevels[resID][b]
      );

      const secondLevel = Object.keys(reservedLevels[resID]).filter(
        k => reservedLevels[resID][k] === 2
      );

      secondLevel.forEach((key, i) => {
        const eventA = events[key];
        const eventB = events[secondLevel[i + 1]];
        if (
          eventA &&
          eventB &&
          ((eventA.startTime <= eventB.startTime && eventB.startTime < eventA.finishTime) ||
            (eventA.startTime < eventB.finishTime && eventB.finishTime <= eventA.finishTime) ||
            (eventA.startTime >= eventB.startTime && eventB.finishTime >= eventA.finishTime))
        ) {
          if (
            parseInt(eventA.reservationModifyDate, 10) > parseInt(eventB.reservationModifyDate, 10)
          ) {
            reservedLevels[resID][key] = 3;
          } else {
            reservedLevels[resID][secondLevel[i + 1]] = 3;
          }
        }
      });

      return {
        ...acc,
        [resID]: {
          maxLevel: reservedLevels[resID][sorted[sorted.length - 1]] || 0,
          1: Object.keys(reservedLevels[resID]).filter(k => reservedLevels[resID][k] === 1),
          2: Object.keys(reservedLevels[resID]).filter(k => reservedLevels[resID][k] === 2),
          3: Object.keys(reservedLevels[resID]).filter(k => reservedLevels[resID][k] === 3)
        }
      };
    }, {});
  }
);

// ---------- EVENT MODAL ---------- //
export const selectEvetnOrgOptions: Selector<ReduxState, void, Option[]> = createSelector(
  state => state.organizations,
  state => state.organizationsList,
  (organizations, list) =>
    list.map(org =>
      getOption(organizations[org].organizationName, organizations[org].organizationUnique)
    )
);

export const selectEventPilotOptions = (pilots = [], eventForm, event, action) =>
  pilots.reduce(
    (acc, pilot) =>
      acc.concat(
        getOption(
          !pilot.used ||
            (pilot.used && event && event.pilotID === pilot.pilotID && action === EVENT_EDIT)
            ? getFullName(pilot, 'pilotName')
            : `${getFullName(pilot, 'pilotName')} – The pilot is busy at that time`,
          pilot.pilotID,
          { disabled: pilot.pilotID === eventForm.pilotID }
        )
      ),
    []
  );

export const selectEventFirstPilotOptions: Selector<ReduxState, void, Option[]> = createSelector(
  state => (state.eventModalData.users ? state.eventModalData.users.pilots : []),
  state => state.eventForm,
  selectEvent,
  state => state.URL.eventParams.action,
  selectEventPilotOptions
);

export const selectEventSICOptions: Selector<ReduxState, void, Option[]> = createSelector(
  state => (state.eventModalData.users ? state.eventModalData.users.sics : []),
  state => state.eventForm,
  selectEvent,
  state => state.URL.eventParams.action,
  selectEventPilotOptions
);

function isAvailable(instructor) {
  return (
    typeof instructor.unavailable === 'undefined' ||
    (typeof instructor.unavailable !== 'undefined' && parseInt(instructor.unavailable, 10) === 0)
  );
}

export const selectEventInstructorOptions: Selector<ReduxState, void, Option[]> = createSelector(
  state => (state.eventModalData.users ? state.eventModalData.users.instructors : []),
  state => state.eventForm,
  selectEvent,
  state => state.URL.eventParams.action,
  (instructors = [], eventForm, event, action) =>
    instructors.reduce(
      (acc, instructor) =>
        acc.concat(
          getOption(
            (!instructor.used && isAvailable(instructor)) ||
              ((instructor.used || !isAvailable(instructor)) &&
                event &&
                event.instructorID === instructor.instructorID &&
                action === EVENT_EDIT)
              ? instructor.name
              : `${instructor.name} – The instructor is ${
                  isAvailable(instructor) ? 'busy' : 'not available'
                } at that time`,
            instructor.instructorID,
            {
              disabled:
                instructor.userID === eventForm.pilotID ||
                instructor.userID === eventForm.secondInComandID
            }
          )
        ),
      []
    )
);

export const selectEventResourcesGroupedOptions: Selector<
  ReduxState,
  void,
  Option[]
> = createSelector(
  state => state.eventModalData.resources,
  state => state.eventModalData.resourcesList || [],
  (resources, list = []) =>
    list.reduce((acc, id) => {
      const resource = resources[id];
      // Skip hidden/unpublished resources missing from the resource map.
      if (!resource) return acc;
      let label = resource.name;

      if (int(resource.used) === 1 && int(resource.reservationCount) === 0) {
        label = label.concat(
          resource.maintenance && resource.maintenance.length
            ? resource.maintenance
            : ' resource is used'
        );
      }

      if (int(resource.reservationCount) > 0) {
        label = label.concat(
          ` - Reserved, ${resource.reservationCount}${
            int(resource.reservationCount) === 3
              ? 'rd'
              : int(resource.reservationCount) === 2
              ? 'nd'
              : int(resource.reservationCount) === 1
              ? 'st'
              : ''
          } in line`
        );
      }

      if (resource.blockedBy && resource.blockedBy.length) {
        label = label.concat(
          `${int(resource.used) === 1 ? ', ' : ' - '} ${resource.blockedBy.join(', ')}`
        );
      }

      if (!acc[resource.typeID]) {
        return {
          ...acc,
          [resource.typeID]: [
            getOption(resource.typeName, resource.typeID, { isGroup: true }),
            getOption(label, resource.id)
          ]
        };
      }

      return {
        ...acc,
        [resource.typeID]: [...acc[resource.typeID], getOption(label, resource.id)]
      };
    }, {})
);

export const selectEventResourcesOptions: Selector<ReduxState, void, Option[]> = createSelector(
  selectEventResourcesGroupedOptions,
  state => state.eventForm.resources,
  (groupedResources, selected) =>
    Object.keys(groupedResources).reduce((acc, key) => {
      const notSelected = groupedResources[key].filter(opt => selected.indexOf(opt.value) === -1);
      return notSelected.length > 1 ? [...acc, ...notSelected] : acc;
    }, [])
);

// ---------- DATE SELECT ---------- //
function getSteps(current, timeFormat) {
  const step = 15; // 15 minutes
  const steps = 24 * 4; // steps in day

  const currentTime = getMinutes(current);

  return Array.apply(null, { length: steps }).reduce(acc => {
    const prevStep = acc[acc.length - 1] ? acc[acc.length - 1].value : -15;
    const nextStep = prevStep + step;

    if (currentTime < nextStep && currentTime > prevStep) {
      acc = acc.concat(getOption(parseTime(currentTime, timeFormat), currentTime));
    }

    acc = acc.concat(getOption(parseTime(nextStep, timeFormat), nextStep));
    return acc;
  }, []);
}

export const selectStartTimeOptions: Selector<ReduxState, void, Option[]> = createSelector(
  state => state.URL.eventParams.startTime,
  state => state.user.settings.time,
  (startTime, timeFormat) => getSteps(startTime, timeFormat)
);

export const selectFinishTimeOptions: Selector<ReduxState, void, Option[]> = createSelector(
  state => state.URL.eventParams.finishTime,
  state => state.user.settings.time,
  (finishTime, timeFormat) => getSteps(finishTime, timeFormat)
);

// ---------- RECURRING ---------- //
export const selectRepsIntervalOptions: Selector<ReduxState, void, Option[]> = createSelector(() =>
  Array.apply(null, { length: 30 }).map((item, i) => getOption(i + 1, i + 1))
);

export const selectRecurringTypeOptionsOptions: Selector<
  ReduxState,
  void,
  Option[]
> = createSelector(
  state => state.eventForm.repsInterval,
  interval => {
    return [
      getOption(pluralize('Day', parseInt(interval, 10)), 'day'),
      getOption(pluralize('Week', parseInt(interval, 10)), 'weeks'),
      getOption(pluralize('Month', parseInt(interval, 10)), 'month')
    ];
  }
);

// ---------- UI MESSAGES ---------- //
export const selectUIMessages: Selector<ReduxState, void, Messages> = (name = DEFAULT_GROUP) =>
  createSelector(
    state => state.UI.messages,
    messages => messages.filter(item => item.group === name)
  );

// ---------- DISCREPANCY ---------- //
export const selectDiscrepancies: Selector<ReduxState, void, Messages> = createSelector(
  state => state.aircraft,
  state =>
    state.aircraft && state.aircraft.discrepanciesList
      ? state.aircraft.discrepanciesList.filter(id => {
          const { aircraft } = state;
          switch (aircraft.discrepancyStatus) {
            case 'closed':
              return aircraft.discrepancies[id].status === 'closed';
            case 'all':
              return aircraft.discrepancies[id].status !== 'closed';
            default:
              return aircraft.discrepancies[id].status === aircraft.discrepancyStatus;
          }
        })
      : [],
  (aircraft, filteredDiscrepancies) =>
    aircraft.sortingField
      ? filteredDiscrepancies.sort(
          makeSort(aircraft.discrepancies, aircraft.sortingField, aircraft.sortingDirection)
        )
      : filteredDiscrepancies
);

export const selectAssignToOptions: Selector<ReduxState, void, Messages> = createSelector(
  state => state.mechanics,
  state => state.mechanicsList,
  (mechanics, list) =>
    list.map(id => getOption(`${mechanics[id].name} ${mechanics[id].surname}`, id))
);

// ---------- CHECKIN ---------- //
export const selectInspectionsStatus: Selector<ReduxState, void, Messages> = createSelector(
  state => state.aircraft,
  aircraft =>
    aircraft && aircraft.inspections
      ? aircraft.inspections.length
        ? aircraft.inspections.filter(
            inspection => inspection.danger === 1 && inspection.affectsAirworthiness === '1'
          ).length
          ? 'Unairworthy'
          : 'Airworthy'
        : 'Unknown'
      : []
);

export const selectCustomResources: Selector<ReduxState, void, Messages> = createSelector(
  state => state.checkinForm,
  form =>
    form.resourcesList
      ? form.resourcesList.filter(id => {
          const resource = form.resources[id];
          // Skip hidden/unpublished resources missing from the resource map.
          return (
            resource &&
            resource.typeUnique !== 'aircraft' &&
            resource.typeUnique !== 'simulator'
          );
        })
      : []
);

// ---------- NO CHANGES IN EVENT ---------- //
export const selectIsNoChangesInEvents: Selector<ReduxState, void, boolean> = createSelector(
  selectEvent,
  state => state.eventForm,
  (event, eventForm) => {
    if (!event.id) return true;
    const formArrivalTime =
      eventForm.expectedArrivalTime && eventForm.expectedArrivalTime.length === 10
        ? eventForm.expectedArrivalTime
        : String(new Date(eventForm.expectedArrivalTime).getTime() / 1000);

    const isSameArrivalTime = formArrivalTime === event.expectedArrivalTime;

    const pilotID = int(event.pilotID) === int(eventForm.pilotID);
    const secondInComandID =
      int(event.secondInComandID) === int(eventForm.secondInComandID) ||
      (!event.secondInComandID && !eventForm.secondInComandID);
    const instructorID =
      int(event.instructorID) === int(eventForm.instructorID) ||
      (!event.instructorID && !eventForm.instructorID);
    const resourcesLength = event.resources.length === eventForm.resources.length;
    const startTime = int(event.startTime) === int(eventForm.startTime);
    const finishTime = int(event.finishTime) === int(eventForm.finishTime);
    const recurring = int(event.recurring) === int(eventForm.recurring);
    const resources = compareResources(event.resources, eventForm.resources);
    const customFields = compareObjects(event.customFields, eventForm.customFields);
    const activityType = event.activityType === eventForm.activityType;
    const comments = event.comments === eventForm.comments;
    const share = event.share === eventForm.share;

    const isEqual =
      pilotID &&
      secondInComandID &&
      instructorID &&
      resourcesLength &&
      startTime &&
      finishTime &&
      recurring &&
      resources &&
      customFields &&
      activityType &&
      comments &&
      share &&
      isSameArrivalTime;

    return isEqual;
  }
);
